import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TaskIcon from '@mui/icons-material/Task';
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  NativeSelect,
  Stack,
  IconButton,
  Collapse,
  TableFooter,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import SearchBox from '../../../Components/Search/SearchBox';
import configServ from '../../../services/config';
import CheckAndReturn from '../../../utility/CheckAndReturn';
import useDebounce from '../../../Components/hooks/useDebounce';
import PurchaseOrderItemTable from '../../../Components/Card/TransactionCards/PurchaseOrder/PurchaseOrderItemTable';
import PurchaseOrderWorkflowPopup from '../../../Components/Card/TransactionCards/PurchaseApprove/PurchaseOrderWorkflowPopup';
import LoaderNew from '../../../Components/Loader/LoaderNew';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Rechecked', value: '1' },
  { label: 'Verified', value: '2' },
  { label: 'Approved', value: '3' },
  { label: 'Rejected', value: '4' || '5' },
];

const headerCellStyle = {
  fontSize: 14,
  fontWeight: 'bold',
  backgroundColor: '#4CB5F5',
  color: 'white',
};

const sortLabelStyle = {
  color: 'white',
  '& .MuiTableSortLabel-icon': { color: 'white !important' },
};

export default function PurchaseRegisterTable({ isChanged, setIsChanged, defaultStatus = '', hideStatusFilter = false, pageTitle = 'Purchase Register' }) {
  const { user_id, company_id, user_name } = useSelector((state) => state.GlobalVariables);
  const [page, setPage] = useState(0);
  const rowsPerPageOptions = [20, 50, 100, 200];
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [purchaseOrderList, setPurchaseOrderList] = useState([]);
  const [searchBy, setSearchBy] = useState('supplier');
  const [search, setSearch] = useState(null);
  const debounceSearch = useDebounce(search, 1000);
  const [sessionYear, setSessionYear] = useState('2026-27');
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [purchaseOrderItemsCache, setPurchaseOrderItemsCache] = useState({});
  const [rowItemsLoading, setRowItemsLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [workflowModal, setWorkflowModal] = useState({ open: false, row: null });
  const [documentModal, setDocumentModal] = useState({ open: false, documents: [] });
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchPurchaseRegisterList();
  }, [isChanged, page, rowsPerPage, debounceSearch, statusFilter, sessionYear]);

  const fetchPurchaseRegisterList = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        user_id,
        company_id,
        page,
        per_page: rowsPerPage,
        search_by: searchBy,
        search: debounceSearch,
        status: statusFilter,
        session_year: sessionYear,
      };
      const res = await configServ.getPurchaseRegisterList(dataToSend);
      if (res.status === 200) {
        setPurchaseOrderList(res.data || []);
        setCount(res.total || 0);
      } else {
        setPurchaseOrderList([]);
        setCount(0);
      }
    } catch (e) {
      console.log(e);
      setPurchaseOrderList([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (stage) => {
    if (stage === null || stage === undefined || stage === 0 || stage === '0') return 'New';
    if (stage === 1 || stage === '1') return 'Rechecked';
    if (stage === 2 || stage === '2') return 'Verified';
    if (stage === 3 || stage === '3') return 'Approved';
    if (stage === 4 || stage === '4') return 'Rejected';
    return 'Unknown';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearchBy = (event) => {
    setSearchBy(event.target.value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortedList = () => {
    if (!sortField) return purchaseOrderList;

    return [...purchaseOrderList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'supplier') {
        aVal = a.supplier ? a.supplier.fname : '';
        bVal = b.supplier ? b.supplier.fname : '';
      }

      if (sortField === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'delivery_date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const toggleRowDetails = async (row) => {
    if (expandedRowId === row.id) {
      setExpandedRowId(null);
      return;
    }

    if (!purchaseOrderItemsCache[row.id]) {
      setRowItemsLoading(row.id);
      try {
        const res = await configServ.getPurchaseOrderItems({ id: row.id, company_id });
        if (res.status === 200) {
          setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: res.data }));
        } else {
          setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: [] }));
        }
      } catch (e) {
        console.log(e);
        setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: [] }));
      } finally {
        setRowItemsLoading(null);
      }
    }
    setExpandedRowId(row.id);
  };

  const handleOpenWorkflow = (row) => {
    setWorkflowModal({ open: true, row });
  };

  const handleCloseWorkflow = () => {
    setWorkflowModal({ open: false, row: null });
  };

  const handleWorkflowSaved = () => {
    setStatusFilter(defaultStatus || '');
    setIsChanged?.((prev) => !prev);
    fetchPurchaseRegisterList();
  };

  const handleViewDocuments = async (row) => {
    try {
      const res = await configServ.getPurchaseOrderWorkflowDocuments({ po_id: row.id });
      if (res.status === 200) {
        setDocumentModal({ open: true, documents: res.data || [] });
      } else {
        setDocumentModal({ open: true, documents: [] });
      }
    } catch (e) {
      console.log('Error fetching documents:', e);
      setDocumentModal({ open: true, documents: [] });
    }
  };

  const handleCloseDocumentModal = () => {
    setDocumentModal({ open: false, documents: [] });
  };

  const getDocumentUrl = (documentPath) => {
    if (!documentPath) return null;
    if (documentPath.startsWith('http://') || documentPath.startsWith('https://')) {
      return documentPath;
    }
    const normalized = documentPath.startsWith('/') ? documentPath : `/${documentPath}`;
    return `${window.location.origin}${normalized}`;
  };

  const isImageDocument = (documentPath) => {
    return !!documentPath && /\.(jpe?g|png|gif|bmp|webp)$/i.test(documentPath);
  };

  const isPdfDocument = (documentPath) => {
    return !!documentPath && /\.pdf$/i.test(documentPath);
  };

  const getWorkflowStageText = (stage, availableUserName, loggedInUserName) => {
    let stageLabel = 'Stage';
    if (stage === 1 || stage === '1') stageLabel = 'Rechecked';
    else if (stage === 2 || stage === '2') stageLabel = 'Verified';
    else if (stage === 3 || stage === '3') stageLabel = 'Approved';
    else if (stage === 4 || stage === '4') stageLabel = 'Rejected';
    const userLabel = availableUserName || loggedInUserName || 'logged in user';
    return `${stageLabel} by ${userLabel}`;
  };

  const handleViewDocument = (documentPath) => {
    const url = getDocumentUrl(documentPath);
    if (!url) return;
    window.open(url, '_blank');
  };

  return (
    <>
      <Paper sx={{ width: '100%' }}>
        <h3 style={{ margin: 0, color: "#1D5FB1", fontFamily: "Helvetica, Arial, sans-serif", fontSize: "24px", textAlign: "center", flex: 1 }}>{pageTitle}</h3>

        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 2, paddingBottom: 0 }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
            {pageTitle}: {count}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container alignItems="flex-end" justifyContent="flex-start" style={{ padding: 10, paddingTop: 0 }} spacing={1} direction={'row'}>
            <Grid item xs={12} md={6} lg={3}>
              <Box sx={{ minWidth: 120, marginLeft: '0px' }}>
                <FormControl fullWidth>
                  <InputLabel variant="standard" htmlFor="search-by-native">
                    Search By
                  </InputLabel>
                  <NativeSelect defaultValue={'contact_name'} onChange={handleSearchBy} inputProps={{ name: 'searchBy', id: 'search-by-native' }}>
                    <option value={'supplier'}>Supplier</option>
                    <option value={'purchase_order_no'}>Purchase Order No</option>
                  </NativeSelect>
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <SearchBox search={search} setSearch={setSearch} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Box sx={{ minWidth: 120, marginLeft: '0px' }}>
                {hideStatusFilter ? (
                  <Box sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, minHeight: 72 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Status
                    </Typography>
                    <Typography>{getStatusLabel(statusFilter)}</Typography>
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel variant="standard" htmlFor="status-native" shrink>
                      Status
                    </InputLabel>
                    <NativeSelect value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }} inputProps={{ name: 'status', id: 'status-native' }}>
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Box sx={{ minWidth: 120, marginLeft: '0px' }}>
                <FormControl fullWidth>
                  <InputLabel variant="standard" htmlFor="session-year-native">
                    Session Year
                  </InputLabel>
                  <NativeSelect value={sessionYear} onChange={(event) => { setSessionYear(event.target.value); setPage(0); }} inputProps={{ name: 'sessionYear', id: 'session-year-native' }}>
                    <option value="2023-24">2023-24</option>
                    <option value="2024-25">2024-25</option>
                    <option value="2025-26">2025-26</option>
                    <option value="2026-27">2026-27</option>
                    <option value="2027-28">2027-28</option>
                  </NativeSelect>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <TableContainer component={Paper} elevation={0} sx={{ display: 'flex', flexDirection: 'column', display: { xs: 'none', sm: 'none', lg: 'block' } }}>
          <Table stickyHeader aria-label="purchase register table">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}></TableCell>
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'supplier'}
                    direction={sortField === 'supplier' ? sortOrder : 'asc'}
                    onClick={() => handleSort('supplier')}
                    sx={sortLabelStyle}
                  >
                    SUPPLIER
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'purchase_order_no'}
                    direction={sortField === 'purchase_order_no' ? sortOrder : 'asc'}
                    onClick={() => handleSort('purchase_order_no')}
                    sx={sortLabelStyle}
                  >
                    ORDER NO
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'total_amount'}
                    direction={sortField === 'total_amount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('total_amount')}
                    sx={sortLabelStyle}
                  >
                    TOTAL AMOUNT
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'delivery_date'}
                    direction={sortField === 'delivery_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('delivery_date')}
                    sx={sortLabelStyle}
                  >
                    DELIVERY DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellStyle}>STATUS</TableCell>
                <TableCell sx={headerCellStyle}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                    <LoaderNew />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {getSortedList().map((row, index) => (
                  <React.Fragment key={row.id}>
                    <TableRow sx={{ '&:hover': { background: '#f1f9fe' } }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowDetails(row)}>
                          <ExpandMoreIcon sx={{ transform: expandedRowId === row.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: '200ms' }} />
                        </IconButton>
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.supplier?.fname || ''}</TableCell>
                      <TableCell>{row.purchase_order_no}</TableCell>
                      <TableCell sx={{ color: 'green' }}>₹{row.total_amount}</TableCell>
                      <TableCell>{CheckAndReturn.transformToDate2(row.delivery_date)}</TableCell>
                      <TableCell>{getStatusLabel(row.latest_workflow_stage)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {row.latest_workflow_stage === 3 || row.latest_workflow_stage === 4 ? (
                          <Tooltip title="View Documents">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocuments(row)}
                              sx={{
                                padding: 0,
                                width: 34,
                                height: 34,
                                color: row.latest_workflow_stage && String(row.latest_workflow_stage) !== '0' ? '#4caf50' : 'inherit',
                              }}
                            >
                              <AttachFileIcon />
                            </IconButton>
                          </Tooltip>
                          ):null}
                          {row.latest_workflow_stage === 2 ? (
                          <Tooltip title="Approve PO">
                            <TaskIcon
                              size="small"
                              onClick={() => handleOpenWorkflow(row)}
                              sx={{ 
                                color: '#bdbdbd',
                                "&:hover": { 
                                  backgroundColor:'rgba(189, 189, 189, 0.1)'
                                }
                              }}
                            >
                              <AssignmentIcon />
                            </TaskIcon>
                          </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={expandedRowId === row.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#f8faff' }}>
                            {rowItemsLoading === row.id ? (
                              <LoaderNew />
                            ) : purchaseOrderItemsCache[row.id] && purchaseOrderItemsCache[row.id].length > 0 ? (
                              <PurchaseOrderItemTable purchaseOrderItems={purchaseOrderItemsCache[row.id]} />
                            ) : (
                              <Typography>No item details available.</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            )}
          </Table>
          <TablePagination rowsPerPageOptions={rowsPerPageOptions} component="div" count={count} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </TableContainer>
      </Paper>
      {/* Mobile View */}
      <Grid container spacing={2} mt={3} sx={{ display: { xs: 'flex', lg: 'none' } }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <LoaderNew />
            </Box>
          </Grid>
        ) : purchaseOrderList.length > 0 ? (
          getSortedList().map((row, index) => (
            <Grid item xs={12} key={row.id}>
              <Accordion expanded={expandedRowId === row.id} onChange={() => toggleRowDetails(row)}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {row.purchase_order_no}
                      </Typography>
                      <Typography variant="body2">Supplier: {row.supplier?.fname || ''}</Typography>
                      <Typography variant="body2">Total: ₹{row.total_amount}</Typography>
                      <Typography variant="body2">Status: {getStatusLabel(row.latest_workflow_stage)}</Typography>
                      {row.latest_workflow_stage === 3 || row.latest_workflow_stage === 4 ? (
                      <Tooltip title="View Attached Documents">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDocuments(row)}
                          sx={{ padding: 0, width: 34, height: 34, color: row.latest_workflow_stage && String(row.latest_workflow_stage) !== '0' ? '#4caf50' : 'inherit' }}
                        >
                          <AttachFileIcon />
                        </IconButton>
                      </Tooltip>
                      ):null}
                      {row.latest_workflow_stage === 2 ? (
                      <Tooltip title='Approve PO'>
                        <TaskIcon
                          size="small"
                          onClick={() => handleOpenWorkflow(row)}
                          sx={{
                            color: '#bdbdbd',
                            '&:hover': {
                              backgroundColor: row.latest_workflow_stage >= 2 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(189, 189, 189, 0.1)'
                            }
                          }}
                        >
                          {row.latest_workflow_stage >= 2 ? <AssignmentTurnedInIcon /> : <AssignmentIcon />}
                        </TaskIcon>
                      </Tooltip>
                      ):null}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {rowItemsLoading === row.id ? (
                    <LoaderNew />
                  ) : purchaseOrderItemsCache[row.id] && purchaseOrderItemsCache[row.id].length > 0 ? (
                    <PurchaseOrderItemTable purchaseOrderItems={purchaseOrderItemsCache[row.id]} />
                  ) : (
                    <Typography>No item details available.</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box sx={{ color: 'red', textAlign: 'center' }}>No data found.</Box>
          </Grid>
        )}
      </Grid>

      <PurchaseOrderWorkflowPopup
        open={workflowModal.open}
        onClose={handleCloseWorkflow}
        poId={workflowModal.row?.id}
        stage={3}
        allowReject={true}
        onSaved={handleWorkflowSaved}
      />

      <Dialog open={documentModal.open} onClose={handleCloseDocumentModal} maxWidth="sm" fullWidth>
        <DialogTitle>Purchase Order Documents</DialogTitle>
        <DialogContent>
          {documentModal.documents.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {documentModal.documents.map((doc, index) => (
                <Box key={doc.id ?? index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {getWorkflowStageText(doc.stage, doc.user_name, user_name)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.created_at ? dayjs(doc.created_at).format('DD-MM-YYYY HH:mm') : 'No timestamp'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {doc.document ? (
                        isPdfDocument(doc.document) ? (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <PictureAsPdfIcon color="error" />
                          </IconButton>
                        ) : isImageDocument(doc.document) ? (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <img
                              src={getDocumentUrl(doc.document)}
                              alt={doc.document.split('/').pop()}
                              style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain', borderRadius: 4, border: '1px solid #ddd' }}
                              onClick={() => handleViewDocument(doc.document)}
                            />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <DescriptionIcon color="action" />
                          </IconButton>
                        )
                      ) : (
                        <Tooltip title="No document attached">
                          <IconButton size="small" disabled>
                            <AttachFileIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Remarks:</strong> {doc.remark ? doc.remark : doc.remarks ? doc.remarks : 'No remarks provided.'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>No documents available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocumentModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
